import React, { cloneElement, useState } from 'react';
import { shape, func, arrayOf, object, string, number, oneOfType, array, node, bool } from 'prop-types';
import { Burger, ShoppingBag } from '@magalu/stereo-ui-icons';
import { Container, Content, AlignIcon, Link } from './header.styles';
import { Strip, Logo } from '../../atoms';
import SidebarMenu from '../SidebarMenu';
import { IconButton, SearchInput } from '../../molecules';
import { HeaderLinks, Sidebar } from '../../organisms';
/**
 * The `<Header />` default component.
 */

function Header({
  isRendered,
  customSidebar,
  onMenuHeaderClick,
  onLogoClick,
  onMenuItemClick,
  onLogout,
  onSearch,
  onShoppingBagClick,
  onTypeChange,
  onHeaderSuggestionLinkClick,
  onSuggestItemClick,
  onProductItemClick,
  onTopTermItemClick,
  onInputClear,
  onInputClose,
  onInputOpen,
  initialValue,
  data,
  linksSuggestions,
  suggestData,
  logoProps,
  sideMenuOpen,
  topTermsData
}) {
  var _headerData$menu, _headerData$logo2, _headerData$logo3, _headerData$shoppingB, _headerData$shoppingB2, _headerData$search;

  const [open, setOpen] = useState(sideMenuOpen);
  const {
    menuData,
    headerData
  } = data;

  const handleMenuHeaderClick = (e, item) => {
    setOpen(false);
    onMenuHeaderClick(e, item);
  };

  const handleMenuItemClick = (e, item, positionLabel) => {
    setOpen(false);
    onMenuItemClick(e, item, positionLabel);
  };

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement("header", null, /*#__PURE__*/React.createElement(IconButton, {
    name: "Burger",
    onClick: () => setOpen(!open)
  }, /*#__PURE__*/React.createElement(Burger, {
    role: "button",
    "aria-label": (headerData == null ? void 0 : (_headerData$menu = headerData.menu) == null ? void 0 : _headerData$menu.ariaLabel) || 'abrir o menu',
    "data-testid": "button-burger",
    id: "button-burger",
    color: "#fff"
  })), /*#__PURE__*/React.createElement(Link, {
    onClick: e => {
      var _headerData$logo;

      return onLogoClick(e, {
        url: (headerData == null ? void 0 : (_headerData$logo = headerData.logo) == null ? void 0 : _headerData$logo.url) || '/'
      });
    },
    href: (headerData == null ? void 0 : (_headerData$logo2 = headerData.logo) == null ? void 0 : _headerData$logo2.url) || '/',
    role: "link",
    "data-testid": "logo",
    "aria-label": (headerData == null ? void 0 : (_headerData$logo3 = headerData.logo) == null ? void 0 : _headerData$logo3.ariaLabel) || 'ir para página inicial'
  }, /*#__PURE__*/React.createElement(Logo, logoProps)), /*#__PURE__*/React.createElement(AlignIcon, null, /*#__PURE__*/React.createElement(Link, {
    href: headerData == null ? void 0 : (_headerData$shoppingB = headerData.shoppingBag) == null ? void 0 : _headerData$shoppingB.url,
    onClick: onShoppingBagClick,
    role: "link",
    "aria-label": (headerData == null ? void 0 : (_headerData$shoppingB2 = headerData.shoppingBag) == null ? void 0 : _headerData$shoppingB2.ariaLabel) || 'ir para sacola'
  }, /*#__PURE__*/React.createElement(ShoppingBag, {
    id: "shoppingBag",
    color: "#fff",
    width: 24,
    height: 24
  })))), /*#__PURE__*/React.createElement(SearchInput, {
    onSearch: onSearch,
    onInputClear: onInputClear,
    onSuggestItemClick: onSuggestItemClick,
    onProductItemClick: onProductItemClick,
    onTopTermItemClick: onTopTermItemClick,
    onTypeChange: onTypeChange,
    onInputClose: onInputClose,
    onInputOpen: onInputOpen,
    placeholder: headerData == null ? void 0 : (_headerData$search = headerData.search) == null ? void 0 : _headerData$search.placeholder,
    initialValue: initialValue,
    list: suggestData,
    topTerms: topTermsData
  })), /*#__PURE__*/React.createElement(HeaderLinks, {
    isRendered: isRendered,
    data: linksSuggestions,
    onClick: onHeaderSuggestionLinkClick,
    isMobile: true
  }), /*#__PURE__*/React.createElement(Strip, {
    position: "relative"
  }), /*#__PURE__*/React.createElement(Sidebar, {
    visible: open,
    bg: "base",
    onClickOutside: () => setOpen(false)
  }, customSidebar ? /*#__PURE__*/cloneElement(customSidebar, {
    data: menuData == null ? void 0 : menuData.data
  }) : /*#__PURE__*/React.createElement(SidebarMenu, {
    data: menuData == null ? void 0 : menuData.data,
    onMenuHeaderClick: handleMenuHeaderClick,
    onLogout: onLogout,
    onMenuItemClick: handleMenuItemClick
  })));
}

process.env.NODE_ENV !== "production" ? Header.propTypes = {
  customSidebar: node,
  data: shape({
    headerData: shape({
      menu: shape({
        ariaLabel: string
      }),
      logo: shape({
        url: string,
        ariaLabel: string
      }),
      shoppingBag: shape({
        url: string,
        ariaLabel: string
      }),
      search: shape({
        placeholder: string
      })
    }),
    menuData: shape({
      data: shape({
        header: shape({
          id: string,
          label: string,
          url: string,
          helpers: arrayOf(shape({
            id: string,
            label: string,
            url: string
          }))
        }),
        specialLinks: arrayOf(shape({
          label: string,
          url: string,
          chevronRight: bool
        })),
        showcaseList: arrayOf(shape({
          name: string,
          value: string
        })),
        categoryList: shape({
          orderBy: arrayOf(string),
          show: number,
          collapseText: string,
          list: arrayOf(shape({
            label: string,
            id: string,
            url: string
          }))
        })
      })
    })
  }),
  linksSuggestions: shape({
    links: arrayOf(shape({
      href: string,
      value: string
    })),
    title: string
  }),
  isRendered: bool,
  sideMenuOpen: bool,
  suggestData: oneOfType([array, object, node]),
  topTermsData: shape({
    list: arrayOf(shape({
      term: string,
      index: number
    })),
    label: string,
    shouldDisplay: bool
  }),
  initialValue: string,
  logoProps: object,
  onCollapsedClick: func,
  onMenuHeaderClick: func,
  onMenuItemClick: func,
  onLogoClick: func,
  onLogout: func,
  onSearch: func.isRequired,
  onShoppingBagClick: func,
  onTypeChange: func.isRequired,
  onInputClear: func,
  onInputClose: func,
  onInputOpen: func,
  onSuggestItemClick: func,
  onProductItemClick: func,
  onTopTermItemClick: func,
  onHeaderSuggestionLinkClick: func
} : void 0;
Header.defaultProps = {
  customSidebar: undefined,
  suggestData: [],
  initialValue: '',
  logoProps: {},
  data: {
    headerData: {},
    menuData: {
      data: {
        header: {
          label: '',
          url: '',
          helpers: []
        },
        moreInformation: []
      }
    }
  },
  linksSuggestions: {
    links: [],
    title: ''
  },
  isRendered: false,
  sideMenuOpen: false,
  onCollapsedClick: () => {},
  onMenuHeaderClick: () => {},
  onMenuItemClick: () => {},
  onLogoClick: () => {},
  onLogout: () => {},
  onInputClear: () => {},
  onShoppingBagClick: () => {},
  onSuggestItemClick: () => {},
  onProductItemClick: () => {},
  onInputClose: () => {},
  onInputOpen: () => {}
};
export default Header;